import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useInventorySummaryStore from '../store/inventorySummaryStore';
import { fetchInventorySummaryCounts } from '../services/inventorySummaryService';
import { SUMMARY_WIDGET_CONFIGS } from '../utils/inventorySummaryWidgets';
import { msalConfig } from '../auth/authConfig';
import { acquireToken } from '../utils/auth';
import { errorHandler } from '../utils/helpers';

interface FetchInventorySummary {
  fetchInventorySummary: () => void;
  loading: boolean;
  error: Error | null;
}

const useFetchInventorySummary = (): FetchInventorySummary => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { setCount, setLoading, setError } = useInventorySummaryStore();
  const [loading, setHookLoading] = useState<boolean>(false);
  const [error, setHookError] = useState<Error | null>(null);

  const fetchInventorySummary = async () => {
    if (accounts.length === 0) {
      navigate('/sign-in');
      return;
    }

    const accessTokenRequest = {
      scopes: [`${msalConfig.auth.clientId}/.default`],
      account: accounts[0],
    };

    setHookLoading(true);
    SUMMARY_WIDGET_CONFIGS.forEach(({ key }) => setLoading(key, true));
    try {
      const accessToken = await acquireToken(instance, accessTokenRequest);
      const counts = await fetchInventorySummaryCounts(accessToken);
      SUMMARY_WIDGET_CONFIGS.forEach(({ key }) => {
        setCount(key, counts[key] ?? 0);
        setLoading(key, false);
      });
    } catch (err) {
      errorHandler(err, setHookError);
      SUMMARY_WIDGET_CONFIGS.forEach(({ key }) => {
        setError(key, true);
        setLoading(key, false);
      });
    } finally {
      setHookLoading(false);
    }
  };

  return { fetchInventorySummary, loading, error };
};

export default useFetchInventorySummary;
